import { ArrowUp } from "lucide-react";
import React from "react";
import { TooltipProps } from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

import { StackedBarImpactColors } from "@intelligems/ig-graphs";
import { IgTypography } from "@intelligems/ig-theme";
import { GraphConfig } from "@intelligems/ig-types";
import { Box, Stack, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { getImpact } from "@src/features/analytics/baseDashboard/utils";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { getStandardMetricByAnalyticsMetricId } from "@src/features/analytics/tables/analyticsAudienceTable/utils";
import { AnalyticsLegendIcon } from "@src/images/Icons/AnalyticsLegendIcon";
import { BarMetricData, StackedBarMetricData } from "@src/types/analytics";

const SPACING_BETWEEN_LABEL_AND_VALUE = 3;

export const IgStackedBarRichAnalyticsTooltip = (
  props: TooltipProps<ValueType, NameType> & {
    config: GraphConfig;
    controlVariationId: string;
    compareVariationId?: string;
    barData: StackedBarMetricData[];
    storeCurrencyConfig: { locale: string; code: string };
    testDuration: number;
    setLegendIconColor?: (params: {
      compareVariationId?: string;
      data: StackedBarMetricData;
      index: number;
    }) => string;
  }
) => {
  const {
    active,
    payload,
    config,
    label,
    controlVariationId,
    compareVariationId,
    barData,
    storeCurrencyConfig,
    testDuration,
    setLegendIconColor,
  } = props;
  const theme = useTheme();
  const graphValueFormatter = useGraphValueFormatter();
  if (!active || !payload) return null;

  const convertedData: BarMetricData[] = barData.map((data) => ({
    id: data.id,
    title: data.title!,
    value: data.impactMetricVal,
    ci_high: 0,
    ci_low: 0,
    p2bb: 0,
    p2bc: 0,
    plus_minus: 0,
    variation_id: data.variation_id!,
    n_visitors: data.n_visitors,
  }));
  const calculatedUplift = getImpact(
    convertedData,
    controlVariationId,
    payload[0].payload.variation_id,
    false,
    storeCurrencyConfig.locale
  );

  return (
    <Box
      sx={{
        padding: "10px",
        borderRadius: "5px",
        backgroundColor: "rgba(73, 76, 80, 1)",
      }}
    >
      <Grid container direction={"column"}>
        <Grid mb={1}>
          <IgTypography
            color={"dark"}
            sx={{
              fontWeight: 600,
            }}
          >
            {label}
          </IgTypography>
        </Grid>
        <Grid container direction={"column"} mb={1} rowSpacing={1}>
          {payload.map((pay, index) => {
            const variationId = pay.payload?.variation_id;
            const cellColor = setLegendIconColor
              ? setLegendIconColor({
                  compareVariationId,
                  data: pay.payload,
                  index,
                })
              : StackedBarImpactColors[index][
                  variationId === compareVariationId ? 0 : 1
                ];
            return (
              <Stack
                key={index}
                sx={{
                  alignItems: "center",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  gap: SPACING_BETWEEN_LABEL_AND_VALUE,
                  justifyContent: "space-between",
                }}
              >
                <Stack
                  sx={{
                    alignItems: "center",
                    flexDirection: "row",
                    gap: 1.5,
                  }}
                >
                  <AnalyticsLegendIcon
                    sx={{ width: "10px", height: "10px" }}
                    fill={cellColor}
                  />

                  <IgTypography
                    color={"dark"}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "110px",
                    }}
                  >
                    {pay.name}
                  </IgTypography>
                </Stack>

                <IgTypography
                  sx={{
                    fontWeight: 600,
                  }}
                  color={"dark"}
                >
                  {graphValueFormatter({
                    isAxisTick: false,
                    value: pay.value as number,
                    configDataTypes: config.dataTypes,
                  })}
                </IgTypography>
              </Stack>
            );
          })}
        </Grid>

        {(() => {
          const contextMetric = config.denominatorMetric ?? "n_visitors";
          const contextValue = payload[0].payload[contextMetric];
          if (!contextValue) return null;
          const contextLabel =
            getStandardMetricByAnalyticsMetricId(contextMetric)?.name ??
            "Visitors";
          return (
            <Grid
              container
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              columnSpacing={SPACING_BETWEEN_LABEL_AND_VALUE}
            >
              <Grid>
                <IgTypography color={"dark"}>{contextLabel}</IgTypography>
              </Grid>
              <Grid>
                <IgTypography color={"dark"} sx={{ fontWeight: 600 }}>
                  {graphValueFormatter({
                    isAxisTick: false,
                    value: contextValue,
                    configDataTypes: ["numberNoDecimal"],
                  })}
                </IgTypography>
              </Grid>
            </Grid>
          );
        })()}

        {controlVariationId === payload[0].payload.variation_id ? null : (
          <Grid
            container
            direction={"row"}
            alignItems={"center"}
            wrap={"nowrap"}
            columnSpacing={SPACING_BETWEEN_LABEL_AND_VALUE}
          >
            <Grid>
              <IgTypography color={"dark"}>
                Total Uplift vs Control
              </IgTypography>
            </Grid>

            <Grid>
              <Grid alignItems={"center"} container>
                {calculatedUplift.change === "none" ? null : (
                  <ArrowUp
                    size={16}
                    color={
                      calculatedUplift.change === "increase"
                        ? theme.igIconColors.success
                        : theme.igIconColors.error
                    }
                    style={{
                      marginRight: "3px",
                      transform:
                        calculatedUplift.change === "decrease"
                          ? "rotate(180deg)"
                          : "none",
                    }}
                  />
                )}
                <IgTypography
                  color={"dark"}
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {calculatedUplift?.impact}
                </IgTypography>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
